import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Button, IconButton, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { useMemo, type Dispatch, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { NoNullFields } from "../../../../server/supabase/base-supabase-service";
import type { ChannelName } from "../../../channels/types";
import type { UsableAccountPersonField } from "../../server/supabase-segment-field-service";
import { ConditionList } from "./condition-list";
import type { SegmentManagerAction, SegmentReducerState } from "./reducer/actions";
import { SegmentAudience } from "./segment-audience";
import { SegmentPreview } from "./segment-preview";
import type { Segment, SegmentMode } from "./types";
import { segmentGroupHasErrors } from "./utils/errors";

interface SegmentGroupProps {
  segment: Segment["segment_json"]["segments"][number];
  segmentIndex: number;
  dispatch: Dispatch<SegmentManagerAction<Segment>>;
  segmentFields: NoNullFields<UsableAccountPersonField>[];
  canDelete: boolean;
  errors: SegmentReducerState<Segment>["errors"];
  channels: ChannelName[];
  disabled: boolean;
  mode: SegmentMode;
  showPreview: boolean;
}

export const SegmentGroup: FC<SegmentGroupProps> = ({
  segment,
  segmentIndex,
  dispatch,
  segmentFields,
  canDelete,
  errors,
  channels,
  disabled,
  mode,
  showPreview,
}) => {
  const { t } = useTranslation();

  const hasErrors = useMemo(
    () => segmentGroupHasErrors(errors, segmentIndex),
    [errors, segmentIndex],
  );

  return (
    <Paper sx={{ p: 2, backgroundColor: "background.default" }}>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" gutterBottom>
            {`Group ${segmentIndex + 1}`}
          </Typography>
          {canDelete && (
            <IconButton
              aria-label="delete-group"
              onClick={() => dispatch({ type: "REMOVE_GROUP", payload: segmentIndex })}
              data-analytics-id="delete-segment-group"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography>{t("Select")}</Typography>
          <SegmentAudience
            segment={segment}
            segmentFields={segmentFields}
            handleFieldChange={(value) =>
              dispatch({
                type: "UPDATE_GROUP_AUDIENCE",
                payload: { index: segmentIndex, recipient_type_key: value },
              })
            }
            disabled={disabled}
            errMsg={
              errors.display
                ? errors.errors?.properties?.segment_json?.properties?.segments?.items?.[
                    segmentIndex
                  ]?.properties?.recipient_type_key?.errors?.join(", ")
                : undefined
            }
            mode={mode}
          />
          <Typography>{t("that match")}</Typography>
          <Select
            id="root-operation"
            size="small"
            value={segment.operation}
            disabled={disabled}
            data-testid="operationHint"
            onChange={(e) =>
              dispatch({
                type: "UPDATE_GROUP_OPERATION",
                payload: { index: segmentIndex, operation: e.target.value },
              })
            }
          >
            <MenuItem value="AND"> {t("all")} </MenuItem>
            <MenuItem value="OR"> {t("any")} </MenuItem>
          </Select>

          <Typography>{t("the following criteria")}</Typography>
        </Stack>

        <Stack
          direction="column"
          spacing={2}
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            p: 4,
            borderRadius: 1,
            borderColor: "divider",
            borderWidth: 1,
            borderStyle: "solid",
          }}
        >
          <ConditionList
            segment={segment}
            segmentIndex={segmentIndex}
            dispatch={dispatch}
            segmentFields={segmentFields}
            errors={errors}
            disabled={disabled}
          />

          {!disabled && (
            <Button
              variant="text"
              onClick={() =>
                dispatch({ type: "ADD_CONDITION", payload: { groupIndex: segmentIndex } })
              }
              data-analytics-id="add-condition-button"
              startIcon={<AddIcon />}
              sx={{ maxWidth: "fit-content" }}
              disabled={disabled}
            >
              {segment.conditions.length === 0
                ? t("Add a condition")
                : segment.operation === "AND"
                  ? t("And ...")
                  : t("Or ...")}
            </Button>
          )}
        </Stack>
        {!hasErrors && segment.conditions.length > 0 && showPreview && (
          <SegmentPreview segment={segment} channels={channels} type="group" />
        )}
      </Stack>
    </Paper>
  );
};
